import { useMemo } from 'react';
import { useApiQuery } from '../../../../common/infrastructure/hooks/useApiQuery';
import { endpointsMetadata } from '../../../../common/infrastructure/routes/endpointsMetadata';
import { IService, IServiceCategory } from '../models/IService';

interface IAppointmentServiceResponse {
  id: number;
  name: string;
  description: string;
  durationInMinutes: number;
  price: number;
  minPrice: number;
  maxPrice: number;
  isActive: boolean;
  categoryId: number | null;
  sortOrder: number;
  isRemote: boolean;
}

interface IAppointmentCategoryResponse {
  id: number;
  name: string;
  sortOrder: number;
}

interface ICategorizedServicesApiResponse {
  categories: IAppointmentCategoryResponse[];
  services: IAppointmentServiceResponse[];
}

/**
 * Hook to fetch available services for appointments, organized by categories
 */
export const useAppointmentServices = (studioId: number | undefined) => {
  const { data: responseData, isLoading, error } = useApiQuery<ICategorizedServicesApiResponse>({
    queryKey: [endpointsMetadata.appointments.getAvailableServices, studioId],
    endpoint: `${endpointsMetadata.appointments.getAvailableServices}?studioId=${studioId}`,
    enabled: !!studioId,
    globalIsLoading: false,
  });

  // Convert backend categories to frontend format
  const categories = useMemo((): IServiceCategory[] => {
    if (!responseData?.categories) return [];

    return responseData.categories.map(cat => ({
      id: cat.id,
      name: cat.name,
      sortOrder: cat.sortOrder,
    }));
  }, [responseData?.categories]);

  // Convert backend services to frontend format
  const services = useMemo((): IService[] => {
    if (!responseData?.services) return [];

    return responseData.services.map(service => ({
      id: service.id,
      name: service.name,
      description: service.description,
      duration: service.durationInMinutes,
      price: service.minPrice, // For backward compatibility
      minPrice: service.minPrice,
      maxPrice: service.maxPrice,
      isActive: service.isActive,
      categoryId: service.categoryId,
      sortOrder: service.sortOrder,
      isRemote: service.isRemote,
    }));
  }, [responseData?.services]);

  // Group services by categoryId for easy consumption
  const servicesByCategory = useMemo((): Record<number | 'uncategorized', IService[]> => {
    const grouped: Record<number | 'uncategorized', IService[]> = {
      uncategorized: [],
    };

    // Initialize groups for each category
    categories.forEach(cat => {
      grouped[cat.id] = [];
    });

    // Group services
    services.forEach(service => {
      if (service.categoryId === null) {
        grouped.uncategorized.push(service);
      } else if (grouped[service.categoryId]) {
        grouped[service.categoryId].push(service);
      } else {
        // Category doesn't exist (shouldn't happen), put in uncategorized
        grouped.uncategorized.push(service);
      }
    });

    return grouped;
  }, [services, categories]);

  return {
    services,
    categories,
    servicesByCategory,
    isLoading,
    error,
    hasData: !!responseData && (responseData.services?.length > 0 || responseData.categories?.length > 0),
  };
};
